import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import customHistory from '../../customHistory';
import { useSelector } from 'react-redux';
import ButtonComponent from './ButtonMC';
import ReportConfirmationScreenMC from './ReportConfirmationScreenMC';
import httpService from '../../services/httpService';
import errorPageImage from 'images/error-page.png';

/**
 * ErrorScreenMC - A component to display when an error occurs while loading information.
 * Provides options to go back to dashboard or report the error to support.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.errorMessage] - The error message (for reporting to support).
 * @param {string} [props.errorStack] - The error stack trace (for reporting to support).
 * @param {string} [props.currentUrl] - The URL where the error occurred.
 * @returns {JSX.Element} The rendered error screen component.
 */
const ErrorScreenMC = ({
  errorMessage = '',
  errorStack = '',
  currentUrl = ''
}) => {
  const { t } = useTranslation('ErrorScreensMC');
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBackToDashboard = () => {
    if (retailer_info?.slug) {
      customHistory.push(`/retailers/${retailer_info.slug}/dashboard`);
    } else {
      customHistory.push('/');
    }
    window.location.reload();
  };

  const handleReportToSupport = async () => {
    setIsSubmitting(true);
    
    try {
      await httpService.post('/api/v1/error_reports', {
        url: currentUrl || window.location.href,
        error_message: errorMessage || 'Error no especificado',
        error_stack: errorStack
      });
    } catch (error) {
      console.error('Error sending report:', error);
    } finally {
      setIsSubmitting(false);
      setShowConfirmation(true);
    }
  };

  if (showConfirmation) {
    return <ReportConfirmationScreenMC onBackToDashboard={handleBackToDashboard} />;
  }

  return (
    <div className="tw-wrapper">
      <div 
        className="md:tw-ml-[60px] tw-min-h-[calc(100vh-60px)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-4"
        style={{ background: 'linear-gradient(180deg, #FFF 55.1%, #EAFEFF 100%)' }}
      >
        {/* Título */}
        <h1 className="tw-text-3xl tw-font-semibold tw-text-blue-500 tw-mb-4 tw-text-center">
          {t('error_screen.title')}
        </h1>

        {/* Descripción */}
        <p className="tw-text-gray-700 tw-mb-6 tw-text-base tw-text-center tw-max-w-lg">
          {t('error_screen.description')}
        </p>

        {/* Imagen de error */}
        <div className="tw-flex tw-justify-center tw-mb-8">
          <img 
            src={errorPageImage} 
            alt="Error" 
            className="tw-w-44 tw-h-auto"
          />
        </div>

        {/* Botones en fila */}
        <div className="tw-flex tw-flex-row tw-gap-4 tw-items-center">
          <ButtonComponent
            text={t('error_screen.back_to_dashboard')}
            variant="SECONDARY"
            onClick={handleBackToDashboard}
            size="medium"
            outlined
          />
          
          <ButtonComponent
            text={t('error_screen.report_to_support')}
            variant="PRIMARY"
            onClick={handleReportToSupport}
            size="medium"
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ErrorScreenMC;
